import {Link, useLocation, useNavigate} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import React, {useContext, useState} from 'react';

import ListItemButton from '@mui/material/ListItemButton';
import SettingsIcon from '@mui/icons-material/Settings';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';

import styles from './index.module.css';

import {Context} from '../..';
import ChatCategoryMenu from '../ChatCategoryMenu';
import {RATING_ROUTE, REVIEWS_ROUTE} from "../../utils/consts";

const API_URL = process.env.REACT_APP_API_URL || window.API_URL;

const menuItems = [
    {
        text: 'Чат с менеджером',
        path: '/chats',
        icon: '/icons/chat-manager.svg',
    },
    {
        text: 'Новости',
        path: '/news',
        icon: '/icons/news.svg',
    },
    {
        text: 'Акции',
        path: '/promotions',
        icon: '/icons/promotion.svg',
    },
    {
        text: 'Услуги',
        path: '/examples',
        icon: '/icons/examples.svg',
    },
    {
        text: 'Врачи',
        path: '/doctors',
        icon: '/icons/doctor.svg',
    },
    {
        text: 'История баллов',
        path: '/pointshistory',
        icon: '/icons/point.svg',
    },
    {
        text: 'Рассылка',
        path: '/mailing',
        icon: '/icons/chats.svg',
    },
    {
        text: 'Отзывы',
        path: REVIEWS_ROUTE,
        anotherPath: RATING_ROUTE,
        icon: '/icons/online_customer.svg',
    },
    {
        text: 'Пациенты',
        path: '/patients',
        icon: '/icons/patients.svg',
    },
    {
        text: 'Академия',
        path: '/academy',
        icon: '/icons/academy.svg',
    },
];

const CustomListItemText = ({primary, sx}) => (
    <Typography
        variant='body2'
        className={styles.listItemText}
        sx={{
            textAlign: 'center',
            fontSize: '10px',
            fontWeight: 'medium',
            lineHeight: '14px',
            ...sx, // Добавляем полученные стили
        }}
    >
        {primary}
    </Typography>
);

const Overlay = observer(({children}) => {
    const {user, clinic, reviewRequestsStats} = useContext(Context);
    const navigate = useNavigate();
    const location = useLocation();
    const [chatMenuAnchor, setChatMenuAnchor] = useState(null);

    const logout = () => {
        user.setIsAuth(false);
        localStorage.removeItem('token');
    };

    const handleChatMenuOpen = (event) => {
        event.preventDefault();
        setChatMenuAnchor(event.currentTarget);
    };

    const handleChatMenuClose = () => {
        setChatMenuAnchor(null);
    };

    const isPathActive = (currentPath, item) => {
        // Проверяем, начинается ли текущий путь с основного пути или дополнительного
        return (
            currentPath.startsWith(item.path) ||
            (item.anotherPath && currentPath.startsWith(item.anotherPath))
        );
    };

    return (
        <Box display={'flex'} flexDirection={'column'} width={'100%'} height={'100vh'} top={'70px'}>
            <Box className={styles.header}>
                <Box display={'flex'} alignItems={'center'}>
                    <ListItemButton
                        sx={{
                            padding: 0,
                            boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.2)',
                            width: '70px',
                            height: '70px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#F2F2F2',
                            zIndex: '1',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '3px',
                                height: '100%',
                                backgroundColor: isPathActive(location.pathname, {
                                    path: '/settings',
                                    anotherPath: '/adminsreferrals',
                                })
                                    ? '#768ada'
                                    : 'transparent',
                            },
                        }}
                        onClick={() => navigate('/settings')}
                    >
                        <img
                            width={'100%'}
                            height={'100%'}
                            src={API_URL + '/static/' + clinic.logo || '.jpg'}
                            alt=''
                        />
                        <SettingsIcon
                            sx={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                color: '#8a8a8a',
                            }}
                        />
                    </ListItemButton>
                    <Typography variant='h6' noWrap component='div' margin={'0 0 0 20px'}>
                        {clinic.name || 'Клиника'}
                    </Typography>
                </Box>
                <Box display={'flex'} alignItems={'center'} height={'100%'}>
                    <Typography variant='h6' noWrap component='div' paddingRight={3}>
                        {clinic.balance ? `Баланс: ${clinic.balance}₽` : 'Баланс не найден'}
                    </Typography>
                    <Button
                        variant='contained'
                        sx={{
                            borderRadius: 0,
                            padding: 0,
                            height: '100%',
                            width: '120px',
                            backgroundColor: '#3D60EC',
                            fontSize: '12px',
                        }}
                        onClick={!user.isAuth ? () => navigate('/login') : logout}
                    >
                        {user.isAuth ? 'Выйти' : 'Войти'}
                    </Button>
                </Box>
            </Box>
            <CssBaseline/>
            <Box
                position={'relative'}
                display={'flex'}
                height={'calc(100vh - 70px)'}
                width={'100%'}
                top={'70px'}
            >
                <Box className={styles.sidebar}>
                    <List
                        disablePadding
                        sx={{
                            height: 'calc(100vh - 70px)',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {menuItems.map((item) => (
                            <ListItem key={item.text} sx={{display: 'flex', flex: 1}} disablePadding>
                                <ListItemButton
                                    component={item.path === '/chats' ? 'div' : Link}
                                    to={item.path === '/chats' ? undefined : item.path}
                                    onClick={item.path === '/chats' ? handleChatMenuOpen : undefined}
                                    className={styles.listItemButton}
                                    sx={{
                                        padding: 0,
                                        width: '100%',
                                        height: '100%',
                                        justifyContent: 'center',
                                        backgroundColor: isPathActive(location.pathname, item) ? '#D0D4E0' : 'inherit',
                                        '&:hover': {
                                            backgroundColor: isPathActive(location.pathname, item)
                                                ? '#D0D4E0'
                                                : '#f5f5f5',
                                        },
                                        overflow: 'visible',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: '3px',
                                            height: '100%',
                                            backgroundColor: isPathActive(location.pathname, item)
                                                ? '#768ada'
                                                : 'transparent',
                                        },
                                    }}
                                >
                                    <img
                                        src={item.icon}
                                        alt={item.text}
                                        className={styles.icon}
                                        style={{
                                            filter: isPathActive(location.pathname, item) ? 'brightness(100%)' : 'none',
                                        }}
                                    />
                                    {item.path === REVIEWS_ROUTE && reviewRequestsStats.pendingCount > 0 && (
                                        <Badge
                                            badgeContent={reviewRequestsStats.pendingCount}
                                            color='error'
                                            anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                                            sx={{
                                                position: 'absolute',
                                                right: '18px',
                                                top: '12px',
                                                zIndex: 10,
                                                pointerEvents: 'none',
                                            }}
                                        />
                                    )}
                                    <CustomListItemText primary={item.text}/>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>
                <Box className={styles.main}>{children}</Box>
            </Box>

            <ChatCategoryMenu
                anchorEl={chatMenuAnchor}
                open={Boolean(chatMenuAnchor)}
                onClose={handleChatMenuClose}
            />
        </Box>
    );
});

export default Overlay;
